import React, { useEffect, useRef, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, UploadCloud, Check, Palette, Image as ImageIcon, FileText, Sparkles, QrCode, Copy, MessageSquare } from "lucide-react";
import { TemplateEditorPanel } from "@/components/TemplateEditorPanel";

export function ClinicSettingsPanel({ clinicId = null }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accent, setAccent] = useState("");
  const [savingAccent, setSavingAccent] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [buildingForm, setBuildingForm] = useState(false);
  const [logoBust, setLogoBust] = useState(Date.now());
  const logoInput = useRef();
  const pdfInput = useRef();

  const qs = clinicId ? `?clinic_id=${clinicId}` : "";

  const load = async () => {
    try {
      const res = await api.get(`/clinic/settings${qs}`);
      setSettings(res.data);
      setAccent(res.data.accent_color);
    } catch (e) {
      toast.error("Could not load Nimbus settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clinicId]);

  const saveAccent = async (color) => {
    setAccent(color);
    setSavingAccent(true);
    try {
      await api.put("/clinic/branding", { clinic_id: clinicId, accent_color: color });
      toast.success("Accent color updated.");
    } catch (e) {
      toast.error("Could not update color.");
    } finally {
      setSavingAccent(false);
    }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (clinicId) fd.append("clinic_id", clinicId);
    setUploadingLogo(true);
    try {
      await api.post("/clinic/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded.");
      setSettings((s) => ({ ...s, has_logo: true }));
      setLogoBust(Date.now());
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploadingLogo(false);
      if (logoInput.current) logoInput.current.value = "";
    }
  };

  const onPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (clinicId) fd.append("clinic_id", clinicId);
    setBuildingForm(true);
    try {
      const res = await api.post("/clinic/template/from-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Form rebuilt from PDF — ${res.data.field_count} fields, ${res.data.steps.length} sections.`);
      await load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Could not build form from PDF.");
    } finally {
      setBuildingForm(false);
      if (pdfInput.current) pdfInput.current.value = "";
    }
  };

  if (loading) {
    return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }
  const cid = settings?.clinic_id;

  return (
    <div className="space-y-6">
      {/* Check-in link & QR */}
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2"><QrCode className="h-4 w-4 text-slate-400" /><h2 className="font-semibold text-slate-900">Front-desk check-in link & QR</h2></div>
        <p className="mt-1 text-sm text-slate-500">Post the QR at your door or text the link — customers Blitz in from their phone.</p>
        <div className="mt-4 flex flex-col gap-5 sm:flex-row sm:items-center">
          <img src={`${API}/clinic/qr.png${cid ? `?clinic_id=${cid}` : ""}`} alt="Check-in QR code" className="h-40 w-40 rounded-lg border border-slate-200 p-1" data-testid="checkin-qr" />
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <input readOnly value={settings.checkin_url || ""} className="w-full bg-transparent text-sm text-slate-700 outline-none" data-testid="checkin-url" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(settings.checkin_url || ""); toast.success("Check-in link copied."); }} data-testid="copy-checkin-link">
                <Copy className="mr-1.5 h-4 w-4" /> Copy link
              </Button>
              <a href={`sms:?&body=${encodeURIComponent("Complete your check-in before your visit: " + (settings.checkin_url || ""))}`} data-testid="sms-checkin-link">
                <Button size="sm" variant="outline"><MessageSquare className="mr-1.5 h-4 w-4" /> Text link</Button>
              </a>
              <a href={`${API}/clinic/qr.png${cid ? `?clinic_id=${cid}` : ""}`} download="checkin-qr.png" data-testid="download-qr">
                <Button size="sm" variant="outline"><QrCode className="mr-1.5 h-4 w-4" /> Download QR</Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Branding */}
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2"><Palette className="h-4 w-4 text-slate-400" /><h2 className="font-semibold text-slate-900">Accent color</h2></div>
        <p className="mt-1 text-sm text-slate-500">Applied to your patient portal and the generated PDF.</p>
        <div className="mt-4 flex flex-wrap gap-3">
          {(settings.accent_presets || []).map((c) => (
            <button
              key={c}
              onClick={() => saveAccent(c)}
              disabled={savingAccent}
              data-testid={`accent-${c.replace('#','')}`}
              className="relative h-10 w-10 rounded-full transition-transform hover:scale-110"
              style={{ backgroundColor: c, boxShadow: accent === c ? `0 0 0 3px #fff, 0 0 0 5px ${c}` : "none" }}
            >
              {accent === c && <Check className="absolute inset-0 m-auto h-4 w-4 text-white" />}
            </button>
          ))}
        </div>
      </div>

      {/* Logo */}
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2"><ImageIcon className="h-4 w-4 text-slate-400" /><h2 className="font-semibold text-slate-900">Nimbus logo</h2></div>
        <p className="mt-1 text-sm text-slate-500">Shown in your portal header and on the PDF.</p>
        <div className="mt-4 flex items-center gap-4">
          {settings.has_logo ? (
            <img src={`${API}/clinic/logo/${cid}?t=${logoBust}`} alt="logo" className="h-14 w-auto max-w-[160px] rounded-lg border border-slate-200 bg-white object-contain p-1" data-testid="logo-preview" />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-dashed border-slate-300 text-slate-300"><ImageIcon className="h-6 w-6" /></div>
          )}
          <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="logo-input" />
          <Button variant="outline" onClick={() => logoInput.current?.click()} disabled={uploadingLogo} data-testid="logo-upload-btn">
            {uploadingLogo ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-1.5 h-4 w-4" />} Upload logo
          </Button>
        </div>
      </div>

      {/* Paperwork template */}
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-slate-400" /><h2 className="font-semibold text-slate-900">Paperwork form</h2></div>
        <p className="mt-1 text-sm text-slate-500">
          Upload your own fillable PDF and we'll build the online intake form automatically with AI.
        </p>
        <input ref={pdfInput} type="file" accept="application/pdf" className="hidden" onChange={onPdf} data-testid="pdf-input" />
        <Button className="mt-4" style={{ backgroundColor: accent }} onClick={() => pdfInput.current?.click()} disabled={buildingForm} data-testid="pdf-upload-btn">
          {buildingForm ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Building form with AI…</> : <><Sparkles className="mr-1.5 h-4 w-4" /> Upload fillable PDF</>}
        </Button>

        <div className="mt-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Current form ({(settings.template_steps || []).length} sections)</p>
        </div>
      </div>

      {settings.template_steps?.length > 0 && (
        <TemplateEditorPanel
          key={JSON.stringify((settings.template_steps || []).map((s) => s.id))}
          clinicId={cid}
          steps={settings.template_steps}
          accent={accent}
          onSaved={(steps) => setSettings((s) => ({ ...s, template_steps: steps }))}
        />
      )}
    </div>
  );
}
